import React, { useEffect, useState, useRef, useMemo, useCallback } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import config from '../config.js';
import pinPrompt from '../utils/pinPrompt';
import ComponentFilesCard from '../components/ComponentFilesCard';
import ComponentRevisionBadge from '../components/ComponentRevisionBadge';
import ComponentRevisionTrail from '../components/ComponentRevisionTrail';
import NewComponentRevisionModal from '../components/NewComponentRevisionModal';
import ComponentExposureModal from '../components/ComponentExposureModal';
import FreeIssueYieldCard from '../components/FreeIssueYieldCard';
import ConsumableLabelsModal from '../components/ConsumableLabelsModal';
import { fetchConsumableLabels } from '../utils/consumableLabels';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area
} from 'recharts';
import { apiErrorMessage, detailToString } from '../utils/apiError';
// The reads, the search and the stock transaction are shared with the phone Consumables screen
// (features/consumables/consumables.js, docs/mobile-pwa-plan.md "One copy of every action").
import {
  COUNT_CORRECTION_REASON,
  TRANSACTION_REASONS,
  loadConsumable,
  loadConsumables as fetchConsumables,
  loadConsumableTransactions as fetchConsumableTransactions,
  loadReorderAlerts,
  matchesConsumableSearch,
  recordConsumableTransaction,
} from '../features/consumables/consumables';
import { desktopUi } from '../features/desktopUi';
import { shopToday } from '../utils/shopDate';

// ==========================================
// SUB-COMPONENT: USAGE CHART (NEW)
// ==========================================

function ConsumableUsageChart({ transactions }) {
    const chartData = useMemo(() => {
        if (!transactions) return [];
        
        const now = new Date();
        const sixMonthsAgo = new Date();
        sixMonthsAgo.setMonth(now.getMonth() - 5); // Current month + 5 previous
        sixMonthsAgo.setDate(1); // Start from beginning of that month
        
        // Initialize map with last 6 months to ensure zero-filled months appear
        const map = new Map();
        for (let i = 5; i >= 0; i--) {
            const d = new Date();
            d.setDate(1);
            d.setMonth(now.getMonth() - i);
            const key = d.toLocaleString('default', { month: 'short', year: '2-digit' });
            map.set(key, { name: key, usage: 0, rawDate: d });
        }

        // Aggregate usage (negative delta)
        transactions.forEach(t => {
            // A physical-count correction moves stock but is not usage.
            if (t.delta < 0 && !t.is_count_correction) { 
                const d = new Date(t.created_at);
                if (d >= sixMonthsAgo) {
                    const key = d.toLocaleString('default', { month: 'short', year: '2-digit' });
                    // Only add if the month is in our generated range (handles slight date edge cases)
                    if (map.has(key)) {
                        const entry = map.get(key);
                        entry.usage += Math.abs(t.delta);
                    }
                }
            }
        });

        return Array.from(map.values());
    }, [transactions]);

    // Don't render if no item is selected (transactions is empty array on load, but valid)
    // We render even if empty to show "0 usage" graph which is informative.

    return (
        <div className="card mb-4 border-secondary bg-white shadow-sm">
            <div className="card-header py-1 text-secondary fw-bold small">
                📉 6-Month Usage Trend
            </div>
            <div className="card-body p-2" style={{ height: '200px' }}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="name" tick={{fontSize: 10}} interval={0} />
                        <YAxis tick={{fontSize: 10}} width={30} allowDecimals={false} />
                        <Tooltip 
                            contentStyle={{fontSize: '12px', padding: '5px'}}
                            formatter={(value) => [value, 'Units Consumed']}
                            cursor={{fill: '#f0f0f0'}}
                        />
                        <Bar dataKey="usage" fill="#6c757d" radius={[4, 4, 0, 0]} barSize={30} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

// ==========================================
// SUB-COMPONENT: LIFESPAN CHART (NEW)
// ==========================================
function ConsumableLifespanChart({ consumableId, apiBase }) {
    const [chartData, setChartData] = useState([]);

    useEffect(() => {
        if (!consumableId) {
            setChartData([]);
            return;
        }

        async function fetchData() {
            try {
                const res = await fetch(`${apiBase}/consumables/${consumableId}/lifespan_trend`);
                if (res.ok) {
                    setChartData(await res.json());
                } else {
                    setChartData([]);
                }
            } catch (err) {
                console.error("Failed to load lifespan trend", err);
            }
        }
        fetchData();
    }, [consumableId, apiBase]);

    if (!chartData || chartData.length === 0) return null;

    return (
        <div className="card mb-4 border-success bg-white shadow-sm">
            <div className="card-header py-1 text-success fw-bold small">
                ⏱️ Historical Run Time Per Unit
            </div>
            <div className="card-body p-2" style={{ height: '200px' }}>
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                        <defs>
                            <linearGradient id="colorLifespan" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8}/>
                                <stop offset="95%" stopColor="#82ca9d" stopOpacity={0}/>
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="date" tick={{fontSize: 10}} interval={0} />
                        <YAxis tick={{fontSize: 10}} width={30} />
                        <Tooltip 
                            contentStyle={{fontSize: '12px', padding: '5px'}}
                            formatter={(value) => [`${value} hrs`, 'Avg Run Time']}
                            labelFormatter={(label) => `Swapped on ${label}`}
                        />
                        <Area 
                            type="monotone" 
                            dataKey="lifespan_hours" 
                            stroke="#82ca9d" 
                            fillOpacity={1} 
                            fill="url(#colorLifespan)" 
                            name="Avg Hours"
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

// ==========================================
// SUB-COMPONENT: ITEM-LEVEL METRICS
// ==========================================
function ConsumableMetrics({ consumableId, apiBase }) {
    const [metrics, setMetrics] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!consumableId) return;
        
        async function fetchMetrics() {
            setLoading(true);
            try {
                const res = await fetch(`${apiBase}/consumables/${consumableId}/metrics`);
                if (res.ok) {
                    setMetrics(await res.json());
                } else {
                    // Reset on error
                    setMetrics({ avg_life_hours: 0, total_runtime_hours: 0, cost_per_hour: 0, current_life_hours: 0 });
                }
            } catch (err) {
                console.error("Failed to load metrics", err);
                setMetrics({ avg_life_hours: 0, total_runtime_hours: 0, cost_per_hour: 0, current_life_hours: 0 });
            } finally {
                setLoading(false);
            }
        }
        fetchMetrics();
    }, [consumableId, apiBase]);

    if (loading) return <div className="text-muted small mb-3">Loading performance stats...</div>;
    
    // Default values
    const m = metrics || { avg_life_hours: 0, total_runtime_hours: 0, cost_per_hour: 0, current_life_hours: 0 };

    // Calculate Health Status
    // Green: < 100% of Avg, Yellow: > 100%, Red: > 125% (Overdue)
    let healthColor = "text-success";
    let healthIcon = "🟢";
    if (m.avg_life_hours > 0) {
        const percentUsed = (m.current_life_hours / m.avg_life_hours) * 100;
        if (percentUsed > 125) {
            healthColor = "text-danger fw-bold";
            healthIcon = "🔴";
        } else if (percentUsed > 100) {
            healthColor = "text-warning fw-bold";
            healthIcon = "🟡";
        }
    }

    return (
        <div className="card mb-4 border-primary bg-light">
            <div className="card-header py-1 text-primary fw-bold small d-flex justify-content-between align-items-center">
                <span>⚡ Performance Metrics</span>
                <span className="badge bg-primary rounded-pill">Live Data</span>
            </div>
            <div className="card-body py-2">
                <div className="row text-center">
                    {/* Column 1: Current Runtime (The New Feature) */}
                    <div className="col-3 border-end">
                        <div className="small text-muted" style={{fontSize: '0.75rem'}}>Current Life</div>
                        <div className={`fw-bold ${healthColor}`} title="Runtime on current unit">
                            {healthIcon} {m.current_life_hours} hrs
                        </div>
                    </div>

                    {/* Column 2: Average Life */}
                    <div className="col-3 border-end">
                        <div className="small text-muted" style={{fontSize: '0.75rem'}}>Avg Life</div>
                        <div className="fw-bold">{m.avg_life_hours} hrs</div>
                    </div>

                    {/* Column 3: Total History */}
                    <div className="col-3 border-end">
                        <div className="small text-muted" style={{fontSize: '0.75rem'}}>Total Run</div>
                        <div className="fw-bold">{m.total_runtime_hours} hrs</div>
                    </div>

                    {/* Column 4: Cost */}
                    <div className="col-3">
                        <div className="small text-muted" style={{fontSize: '0.75rem'}}>Est. Cost</div>
                        <div className="fw-bold text-success">${m.cost_per_hour}<span style={{fontSize:'0.7rem'}}>/hr</span></div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ==========================================
// SUB-COMPONENT: GLOBAL LASER ANALYTICS (UPDATED)
// ==========================================
function LaserAnalytics({ apiBase }) {
    const [data, setData] = useState({ 
        kpis: { gas_efficiency_per_hr: 0, avg_nozzle_life_hours: 0, monthly_spend: 0 },
        gas_stats: [], 
        nozzle_stats: [] 
    });
    const [trends, setTrends] = useState({
        gas_trend: [],
        nozzle_life_trend: [],
        velocity_trend: [],
        premature_rate: 0
    });
    const [loading, setLoading] = useState(false);
    
    // NEW: State for the recalc button
    const [recalcLoading, setRecalcLoading] = useState(false);

    // Function to reload data
    async function fetchData() {
        setLoading(true);
        try {
            const resStats = await fetch(`${apiBase}/reports/laser_analytics`);
            if (resStats.ok) setData(await resStats.json());

            const resTrends = await fetch(`${apiBase}/reports/laser_trends`);
            if (resTrends.ok) setTrends(await resTrends.json());

        } catch (err) {
            console.error("Failed to load analytics", err);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchData();
    }, [apiBase]);

    // NEW: Handler for the Recalc History button
    async function handleRecalcHistory() {
        if (!window.confirm("⚠️ Recalculate History?\n\nThis will update historical nozzle & lens usage records to match current 'Beam-On' times for parts. This cannot be undone.\n\nContinue?")) {
            return;
        }

        setRecalcLoading(true);
        try {
            const res = await fetch(`${apiBase}/admin/recalc_consumable_history`, { 
                method: 'POST' 
            });
            
            if (res.ok) {
                const result = await res.json();
                alert("✅ Success: " + result.message);
                // Refresh the charts to show the corrected data
                fetchData();
            } else {
                const errText = await res.text();
                alert("❌ Failed: " + errText);
            }
        } catch (e) {
            alert("❌ Error: " + e.message);
        } finally {
            setRecalcLoading(false);
        }
    }

    const colors = ["#8884d8", "#82ca9d", "#ffc658", "#ff7300", "#0088fe", "#00C49F"];

    const velocityKeys = useMemo(() => {
        const keys = new Set();
        // Check if trends.velocity_trend exists (it defaults to [] in state, so this is safe)
        if (trends.velocity_trend) {
            trends.velocity_trend.forEach(item => {
                Object.keys(item).forEach(k => {
                    if (k !== 'week') keys.add(k);
                });
            });
        }
        return Array.from(keys).sort();
    }, [trends.velocity_trend]);

    // NOW check for loading
    if (loading) return <div className="p-4 text-center">Loading Analytics...</div>;

    // This is not a hook, so it is fine to stay here
    const nozzleKeys = trends.nozzle_life_trend.length > 0 
        ? Object.keys(trends.nozzle_life_trend[0]).filter(k => k !== 'month') 
        : [];

    return (
        <div className="container-fluid">
            {/* --- HEADER ROW WITH RECALC BUTTON --- */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="mb-0 text-muted">Laser Consumable Analytics</h5>
                <button 
                    className="btn btn-sm btn-outline-danger"
                    onClick={handleRecalcHistory}
                    disabled={recalcLoading}
                    title="Apply current beam-on times to historical usage logs"
                >
                    {recalcLoading ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Updating History...
                        </>
                    ) : (
                        "🔄 Recalc History"
                    )}
                </button>
            </div>

            {/* --- TOP ROW: KPIs --- */}
            <div className="row g-4 mb-4">
                <div className="col-md-4">
                    <div className="card border-primary h-100">
                        <div className="card-body text-center">
                            <div className="text-muted small text-uppercase">Gas Efficiency</div>
                            <div className="display-6 fw-bold text-primary">${data.kpis?.gas_efficiency_per_hr}</div>
                            <div className="small text-muted">per beam-on hour</div>
                        </div>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="card border-success h-100">
                        <div className="card-body text-center">
                            <div className="text-muted small text-uppercase">Avg Nozzle Life</div>
                            <div className="display-6 fw-bold text-success">{data.kpis?.avg_nozzle_life_hours} h</div>
                            <div className="small text-muted">across all types</div>
                        </div>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="card border-warning h-100">
                        <div className="card-body text-center">
                            <div className="text-muted small text-uppercase">Monthly Consumable Spend</div>
                            <div className="display-6 fw-bold text-warning">${data.kpis?.monthly_spend.toLocaleString()}</div>
                            <div className="small text-muted">current month</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* --- MIDDLE ROW: TRENDS --- */}
            <div className="row g-4 mb-4">
                {/* Left: Gas Efficiency Trend */}
                <div className="col-md-6">
                    <div className="card h-100 shadow-sm">
                        <div className="card-header bg-white fw-bold">📉 Gas Efficiency Trend ($/Hr)</div>
                        <div className="card-body" style={{ height: '300px' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={trends.gas_trend}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="month" />
                                    <YAxis prefix="$" />
                                    <Tooltip formatter={(value) => `$${value}`} />
                                    <Area type="monotone" dataKey="efficiency" stroke="#8884d8" fill="#8884d8" name="Cost/Hr" />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                {/* Right: Nozzle Life by Type */}
                <div className="col-md-6">
                    <div className="card h-100 shadow-sm">
                        <div className="card-header bg-white fw-bold">⏳ Nozzle Life by Type (Avg Hrs)</div>
                        <div className="card-body" style={{ height: '300px' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={trends.nozzle_life_trend}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="month" />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    {nozzleKeys.map((key, index) => (
                                        <Line 
                                            key={key} 
                                            type="monotone" 
                                            dataKey={key} 
                                            stroke={colors[index % colors.length]} 
                                            strokeWidth={2}
                                            connectNulls
                                        />
                                    ))}
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            </div>

            {/* --- BOTTOM ROW: DRILL DOWN --- */}
            <div className="row g-4 mb-4">
                {/* Left: Consumption Velocity */}
                <div className="col-md-8">
                    <div className="card h-100 shadow-sm">
                        <div className="card-header bg-white fw-bold">🔥 Nozzle Consumption Velocity (Units per Week)</div>
                        <div className="card-body" style={{ height: '300px' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={trends.velocity_trend}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="week" />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    {velocityKeys.map((key, index) => (
                                        <Bar 
                                            key={key} 
                                            dataKey={key} 
                                            stackId="a" 
                                            fill={colors[index % colors.length]} 
                                        />
                                    ))}
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                {/* Right: Premature Failure Rate */}
                <div className="col-md-4">
                    <div className="card h-100 shadow-sm text-center">
                        <div className="card-header bg-white fw-bold">⚠️ Premature Failure Rate</div>
                        <div className="card-body d-flex flex-column justify-content-center align-items-center">
                            <div 
                                className="rounded-circle d-flex justify-content-center align-items-center mb-3"
                                style={{
                                    width: '150px', 
                                    height: '150px', 
                                    border: `10px solid ${trends.premature_rate > 20 ? '#dc3545' : trends.premature_rate > 10 ? '#ffc107' : '#198754'}`,
                                    fontSize: '2rem',
                                    fontWeight: 'bold',
                                    color: '#333'
                                }}
                            >
                                {trends.premature_rate}%
                            </div>
                            <p className="text-muted small px-3">
                                Percentage of nozzles that lasted <strong>&lt; 75%</strong> of the average life for their type.
                            </p>
                            {trends.premature_rate > 20 && (
                                <div className="alert alert-danger py-1 px-2 w-100 small mb-0">
                                    High Failure Rate! Check machine alignment or nesting quality.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* --- TABLE DATA (Existing) --- */}
            <div className="row g-4">
                <div className="col-md-6">
                    <div className="card shadow-sm">
                        <div className="card-header bg-white fw-bold">Detailed Gas Stats</div>
                        <div className="card-body p-0">
                            <table className="table table-sm table-striped mb-0">
                                <thead>
                                    <tr><th>Type</th><th className="text-end">Hrs</th><th className="text-end">Cost</th><th className="text-end">$/Hr</th></tr>
                                </thead>
                                <tbody>
                                    {data.gas_stats.map((r, i) => (
                                        <tr key={i}>
                                            <td>{r.gas_type}</td>
                                            <td className="text-end">{r.total_hours}</td>
                                            <td className="text-end">${r.total_gas_cost}</td>
                                            <td className="text-end fw-bold">${r.cost_per_hour}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="card shadow-sm">
                        <div className="card-header bg-white fw-bold">Detailed Nozzle Stats</div>
                        <div className="card-body p-0">
                            <table className="table table-sm table-striped mb-0">
                                <thead>
                                    <tr><th>Model</th><th className="text-end">Used</th><th className="text-end">Avg Life</th></tr>
                                </thead>
                                <tbody>
                                    {data.nozzle_stats.map((r, i) => (
                                        <tr key={i}>
                                            <td>{r.nozzle_model}</td>
                                            <td className="text-end">{r.qty_consumed}</td>
                                            <td className="text-end fw-bold">{r.avg_life_hours} h</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ==========================================
// SUB-COMPONENT: GAS SESSIONS MANAGER
// ==========================================
function GasSessionsManager({ apiBase }) {
  const [gasType, setGasType] = useState("N2");
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionDetail, setSessionDetail] = useState(null);
  const [dateRange, setDateRange] = useState("90");
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [alertMsg, setAlertMsg] = useState(null);

  // Modal state
  const [showUndoModal, setShowUndoModal] = useState(false);
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRecalcModal, setShowRecalcModal] = useState(false);
  const [modalReason, setModalReason] = useState("");
  const [splitTime, setSplitTime] = useState("");
  const [editStartTime, setEditStartTime] = useState("");
  const [editEndTime, setEditEndTime] = useState("");
  const [recalcStartDate, setRecalcStartDate] = useState("");
  const [recalcEndDate, setRecalcEndDate] = useState("");

  const loadSessions = useCallback(async () => {
    setLoading(true);
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - parseInt(dateRange));
      const params = new URLSearchParams({
        gas_type: gasType,
        start_date: startDate.toISOString().split("T")[0],
        limit: "200",
      });
      const resp = await fetch(`${apiBase}/gas_sessions?${params}`);
      if (!resp.ok) throw new Error("Failed to load sessions");
      const data = await resp.json();
      setSessions(data);
    } catch (e) {
      console.error(e);
      setAlertMsg({ type: "danger", text: "Failed to load gas sessions" });
    } finally {
      setLoading(false);
    }
  }, [apiBase, gasType, dateRange]);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  const loadSessionDetail = async (sessionId) => {
    try {
      const resp = await fetch(`${apiBase}/gas_sessions/${sessionId}`);
      if (!resp.ok) throw new Error("Failed to load session detail");
      const data = await resp.json();
      setSessionDetail(data);
    } catch (e) {
      console.error(e);
      setAlertMsg({ type: "danger", text: "Failed to load session detail" });
    }
  };

  const handleSelectSession = (s) => {
    if (selectedSession === s.session_id) {
      setSelectedSession(null);
      setSessionDetail(null);
    } else {
      setSelectedSession(s.session_id);
      loadSessionDetail(s.session_id);
    }
  };

  const showAlert = (type, text) => {
    setAlertMsg({ type, text });
    setTimeout(() => setAlertMsg(null), 6000);
  };

  // ─── UNDO SWAP ─────────────────────────────────
  const handleUndoSwap = async () => {
    if (!modalReason.trim()) return alert("Please provide a reason.");
    const pin = await pinPrompt("Enter PIN to undo this swap:");
    if (!pin) return;
    setActionLoading(true);
    try {
      const resp = await fetch(`${apiBase}/gas_sessions/${selectedSession}/undo`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin, reason: modalReason }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(detailToString(data.detail) || "Undo failed");
      showAlert("success", data.message);
      setShowUndoModal(false);
      setModalReason("");
      setSelectedSession(null);
      setSessionDetail(null);
      loadSessions();
    } catch (e) {
      showAlert("danger", e.message);
    } finally {
      setActionLoading(false);
    }
  };

  // ─── SPLIT SESSION ─────────────────────────────
  const handleSplitSession = async () => {
    if (!modalReason.trim()) return alert("Please provide a reason.");
    if (!splitTime) return alert("Please select a split time.");
    const pin = await pinPrompt("Enter PIN to insert manual swap:");
    if (!pin) return;
    setActionLoading(true);
    try {
      const resp = await fetch(`${apiBase}/gas_sessions/${selectedSession}/split`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin, split_time: new Date(splitTime).toISOString(), reason: modalReason }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(detailToString(data.detail) || "Split failed");
      showAlert("success", data.message);
      setShowSplitModal(false);
      setModalReason("");
      setSplitTime("");
      setSelectedSession(null);
      setSessionDetail(null);
      loadSessions();
    } catch (e) {
      showAlert("danger", e.message);
    } finally {
      setActionLoading(false);
    }
  };

  // ─── EDIT SESSION ──────────────────────────────
  const handleEditSession = async () => {
    if (!modalReason.trim()) return alert("Please provide a reason.");
    if (!editStartTime && !editEndTime) return alert("Provide at least one time to change.");
    const pin = await pinPrompt("Enter PIN to edit session times:");
    if (!pin) return;
    setActionLoading(true);
    try {
      const body = { pin, reason: modalReason };
      if (editStartTime) body.start_time = new Date(editStartTime).toISOString();
      if (editEndTime) body.end_time = new Date(editEndTime).toISOString();
      const resp = await fetch(`${apiBase}/gas_sessions/${selectedSession}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(detailToString(data.detail) || "Edit failed");
      showAlert("success", data.message);
      setShowEditModal(false);
      setModalReason("");
      setEditStartTime("");
      setEditEndTime("");
      loadSessions();
      if (selectedSession) loadSessionDetail(selectedSession);
    } catch (e) {
      showAlert("danger", e.message);
    } finally {
      setActionLoading(false);
    }
  };

  // ─── BULK RECALCULATE ──────────────────────────
  const handleRecalculate = async () => {
    if (!recalcStartDate || !recalcEndDate) return alert("Select date range.");
    const pin = await pinPrompt("Enter PIN to recalculate gas sessions:");
    if (!pin) return;
    setActionLoading(true);
    try {
      const resp = await fetch(`${apiBase}/gas_sessions/recalculate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin, gas_type: gasType, start_date: recalcStartDate, end_date: recalcEndDate }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(detailToString(data.detail) || "Recalculation failed");
      showAlert("success", data.message);
      setShowRecalcModal(false);
      setRecalcStartDate("");
      setRecalcEndDate("");
      loadSessions();
      if (selectedSession) loadSessionDetail(selectedSession);
    } catch (e) {
      showAlert("danger", e.message);
    } finally {
      setActionLoading(false);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) + " " +
           d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  };

  const fmtDuration = (hours) => {
    if (!hours) return "—";
    if (hours < 1) return `${Math.round(hours * 60)}m`;
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };

  // Convert ISO to datetime-local input value
  const toLocalInput = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const offset = d.getTimezoneOffset();
    const local = new Date(d.getTime() - offset * 60000);
    return local.toISOString().slice(0, 16);
  };

  return (
    <div>
      {/* Alert */}
      {alertMsg && (
        <div className={`alert alert-${alertMsg.type} alert-dismissible fade show`} role="alert">
          {alertMsg.text}
          <button type="button" className="btn-close" onClick={() => setAlertMsg(null)}></button>
        </div>
      )}

      {/* Top Controls */}
      <div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
        <div className="btn-group" role="group">
          <button className={`btn btn-sm ${gasType === "N2" ? "btn-primary" : "btn-outline-primary"}`} onClick={() => setGasType("N2")}>
            Nitrogen (N2)
          </button>
          <button className={`btn btn-sm ${gasType === "O2" ? "btn-primary" : "btn-outline-primary"}`} onClick={() => setGasType("O2")}>
            Oxygen (O2)
          </button>
        </div>
        <select className="form-select form-select-sm" style={{ width: 140 }} value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
          <option value="180">Last 6 months</option>
          <option value="365">Last year</option>
        </select>
        <button className="btn btn-sm btn-outline-secondary" onClick={loadSessions} disabled={loading}>
          {loading ? <span className="spinner-border spinner-border-sm me-1"></span> : null}
          Refresh
        </button>
        <button className="btn btn-sm btn-outline-warning ms-auto" onClick={() => setShowRecalcModal(true)}>
          Recalculate Range
        </button>
      </div>

      {/* Sessions Table */}
      <div className="table-responsive">
        <table className="table table-sm table-hover align-middle">
          <thead className="table-light">
            <tr>
              <th style={{ width: 40 }}>#</th>
              <th>Status</th>
              <th>Start</th>
              <th>End</th>
              <th>Duration</th>
              <th className="text-end">Tasks</th>
              <th className="text-end">Gas (CF)</th>
              <th className="text-end">Cost</th>
            </tr>
          </thead>
          <tbody>
            {sessions.length === 0 && !loading && (
              <tr><td colSpan={8} className="text-center text-muted py-3">No sessions found</td></tr>
            )}
            {sessions.map((s) => (
              <React.Fragment key={s.session_id}>
                <tr
                  onClick={() => handleSelectSession(s)}
                  style={{ cursor: "pointer", background: selectedSession === s.session_id ? "#e8f0fe" : undefined }}
                >
                  <td className="text-muted">{s.session_id}</td>
                  <td>
                    {s.is_active ? (
                      <span className="badge bg-success">Active</span>
                    ) : (
                      <span className="badge bg-secondary">Closed</span>
                    )}
                  </td>
                  <td>{fmtDate(s.start_time)}</td>
                  <td>{s.is_active ? <em className="text-muted">ongoing</em> : fmtDate(s.end_time)}</td>
                  <td>{fmtDuration(s.duration_hours)}</td>
                  <td className="text-end">{s.task_count}</td>
                  <td className="text-end">{s.total_gas_used_cf.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                  <td className="text-end">{s.actual_cost != null ? `$${s.actual_cost.toFixed(2)}` : "—"}</td>
                </tr>

                {/* Detail Panel */}
                {selectedSession === s.session_id && sessionDetail && (
                  <tr>
                    <td colSpan={8} className="p-0">
                      <div className="border-top bg-light p-3">
                        {/* Action Buttons */}
                        <div className="d-flex gap-2 mb-3">
                          <button className="btn btn-sm btn-outline-danger" onClick={() => { setModalReason(""); setShowUndoModal(true); }}>
                            Undo Swap
                          </button>
                          <button className="btn btn-sm btn-outline-primary" onClick={() => { setModalReason(""); setSplitTime(""); setShowSplitModal(true); }}>
                            Split Session
                          </button>
                          <button className="btn btn-sm btn-outline-secondary" onClick={() => {
                            setModalReason("");
                            setEditStartTime(toLocalInput(sessionDetail.session.start_time));
                            setEditEndTime(toLocalInput(sessionDetail.session.end_time));
                            setShowEditModal(true);
                          }}>
                            Edit Times
                          </button>
                        </div>

                        {/* Session Summary */}
                        <div className="row mb-3">
                          <div className="col-md-3">
                            <small className="text-muted d-block">Total Gas Used</small>
                            <strong>{sessionDetail.session.total_theoretical_cf ? `${parseFloat(sessionDetail.session.total_theoretical_cf).toLocaleString(undefined, {maximumFractionDigits: 0})} CF` : "—"}</strong>
                          </div>
                          <div className="col-md-3">
                            <small className="text-muted d-block">Tank Cost</small>
                            <strong>{sessionDetail.session.actual_cost ? `$${parseFloat(sessionDetail.session.actual_cost).toFixed(2)}` : "—"}</strong>
                          </div>
                          <div className="col-md-3">
                            <small className="text-muted d-block">Tasks</small>
                            <strong>{sessionDetail.tasks.length}</strong>
                          </div>
                          <div className="col-md-3">
                            <small className="text-muted d-block">Runtime</small>
                            <strong>{fmtDuration(sessionDetail.tasks.reduce((sum, t) => sum + t.runtime_minutes, 0) / 60)}</strong>
                          </div>
                        </div>

                        {/* Tasks Table */}
                        {sessionDetail.tasks.length > 0 && (
                          <div className="table-responsive mb-3">
                            <table className="table table-sm table-bordered mb-0" style={{ fontSize: "0.85rem" }}>
                              <thead>
                                <tr>
                                  <th>Job</th>
                                  <th>Part</th>
                                  <th className="text-end">Runtime</th>
                                  <th className="text-end">Gas (CF)</th>
                                  <th className="text-end">Gas Cost</th>
                                  <th className="text-end">Wear Cost</th>
                                  <th>Nozzle</th>
                                </tr>
                              </thead>
                              <tbody>
                                {sessionDetail.tasks.map((t, i) => (
                                  <tr key={`${t.task_id}-${i}`}>
                                    <td>{t.job_number}</td>
                                    <td>{t.part_number || "—"}</td>
                                    <td className="text-end">{t.runtime_minutes.toFixed(1)} min</td>
                                    <td className="text-end">{t.gas_used_cf.toFixed(1)}</td>
                                    <td className="text-end">${t.gas_cost_est.toFixed(2)}</td>
                                    <td className="text-end">${t.wear_cost_snapshot.toFixed(2)}</td>
                                    <td>{t.nozzle_type || "—"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {/* Audit Log */}
                        {sessionDetail.audit_log.length > 0 && (
                          <div>
                            <h6 className="mb-2">Audit Log</h6>
                            {sessionDetail.audit_log.map((a) => (
                              <div key={a.audit_id} className="small text-muted mb-1">
                                <strong>{fmtDate(a.created_at)}</strong> — {a.action} by {a.changed_by}: {a.summary}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* ─── UNDO MODAL ─────────────────────────── */}
      {showUndoModal && (
        <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Undo Gas Swap</h5>
                <button className="btn-close" onClick={() => setShowUndoModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-warning mb-3">
                  This will merge session <strong>#{selectedSession}</strong> back into its predecessor.
                  The false swap's inventory deduction will be reversed (+1 tank) and gas costs will be recalculated.
                </div>
                <label className="form-label">Reason <span className="text-danger">*</span></label>
                <textarea className="form-control" rows={2} value={modalReason} onChange={(e) => setModalReason(e.target.value)}
                  placeholder="e.g. Sensor misfired, tank was not actually swapped" />
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowUndoModal(false)}>Cancel</button>
                <button className="btn btn-danger" onClick={handleUndoSwap} disabled={actionLoading}>
                  {actionLoading ? <span className="spinner-border spinner-border-sm me-1"></span> : null}
                  Undo Swap
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── SPLIT MODAL ────────────────────────── */}
      {showSplitModal && (
        <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Insert Manual Swap</h5>
                <button className="btn-close" onClick={() => setShowSplitModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info mb-3">
                  This will split session <strong>#{selectedSession}</strong> into two sessions at the specified time.
                  Inventory will be deducted by 1 (the missed swap consumed a tank that was never counted).
                </div>
                <div className="mb-3">
                  <label className="form-label">Split Time <span className="text-danger">*</span></label>
                  <input type="datetime-local" className="form-control" value={splitTime} onChange={(e) => setSplitTime(e.target.value)} />
                  {sessionDetail && (
                    <small className="text-muted">
                      Must be between {fmtDate(sessionDetail.session.start_time)} and {sessionDetail.session.end_time ? fmtDate(sessionDetail.session.end_time) : "now"}
                    </small>
                  )}
                </div>
                <label className="form-label">Reason <span className="text-danger">*</span></label>
                <textarea className="form-control" rows={2} value={modalReason} onChange={(e) => setModalReason(e.target.value)}
                  placeholder="e.g. Operator swapped tank but sensor didn't trigger" />
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowSplitModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleSplitSession} disabled={actionLoading}>
                  {actionLoading ? <span className="spinner-border spinner-border-sm me-1"></span> : null}
                  Split Session
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── EDIT MODAL ─────────────────────────── */}
      {showEditModal && (
        <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Session Times</h5>
                <button className="btn-close" onClick={() => setShowEditModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Start Time</label>
                  <input type="datetime-local" className="form-control" value={editStartTime} onChange={(e) => setEditStartTime(e.target.value)} />
                </div>
                <div className="mb-3">
                  <label className="form-label">End Time</label>
                  <input type="datetime-local" className="form-control" value={editEndTime} onChange={(e) => setEditEndTime(e.target.value)}
                    disabled={sessionDetail?.session.is_active} />
                  {sessionDetail?.session.is_active && <small className="text-muted">Cannot edit end time on active session. Use Split instead.</small>}
                </div>
                <label className="form-label">Reason <span className="text-danger">*</span></label>
                <textarea className="form-control" rows={2} value={modalReason} onChange={(e) => setModalReason(e.target.value)}
                  placeholder="e.g. Correcting swap detection time" />
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleEditSession} disabled={actionLoading}>
                  {actionLoading ? <span className="spinner-border spinner-border-sm me-1"></span> : null}
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── RECALCULATE MODAL ──────────────────── */}
      {showRecalcModal && (
        <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Recalculate {gasType} Sessions</h5>
                <button className="btn-close" onClick={() => setShowRecalcModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-warning mb-3">
                  This will delete and re-run gas allocation for all tasks in the selected date range. Gas costs will be recalculated using current session boundaries.
                </div>
                <div className="row mb-3">
                  <div className="col">
                    <label className="form-label">Start Date</label>
                    <input type="date" className="form-control" value={recalcStartDate} onChange={(e) => setRecalcStartDate(e.target.value)} />
                  </div>
                  <div className="col">
                    <label className="form-label">End Date</label>
                    <input type="date" className="form-control" value={recalcEndDate} onChange={(e) => setRecalcEndDate(e.target.value)} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowRecalcModal(false)}>Cancel</button>
                <button className="btn btn-warning" onClick={handleRecalculate} disabled={actionLoading}>
                  {actionLoading ? <span className="spinner-border spinner-border-sm me-1"></span> : null}
                  Recalculate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==========================================
// MAIN COMPONENT
// ==========================================
export default function ConsumableEntryPage() {
  // ======= Left panel: consumables listing and search =======
  const [consumables, setConsumables] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedConsumable, setSelectedConsumable] = useState(null)
  const [filterType, setFilterType] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [categories, setCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState("");

  // ======= Right panel: consumable detail =======
  const [dbConsumableId, setDbConsumableId] = useState(null)
  const [productId, setProductId] = useState("")
  const [description, setDescription] = useState("")
  const [stockQuantity, setStockQuantity] = useState("")
  const [reorderPoint, setReorderPoint] = useState("")
  const [category, setCategory] = useState("")
  const [notes, setNotes] = useState("")
  const [consumableType, setConsumableType] = useState("consumable")
  const [price, setPrice] = useState("")
  const [weightLbs, setWeightLbs] = useState("")
  const [minOrderQty, setMinOrderQty] = useState("1")
  const [orderInQtyOf, setOrderInQtyOf] = useState("1")

  // ======= Produced By (part yield linkage) =======
  const [producedByPart, setProducedByPart] = useState(null)

  // ======= Component revision control (components only) =======
  // componentDetail is the /components/{id} read: it resolves spec_owner, the
  // governing part for a shadow-twin, and the revision in force. Everything
  // below keys off it, so a plain consumable renders exactly as it did before.
  const [componentDetail, setComponentDetail] = useState(null)
  const [componentDocs, setComponentDocs] = useState([])
  const [componentRefreshKey, setComponentRefreshKey] = useState(0)
  const [showRevisionModal, setShowRevisionModal] = useState(false)
  const [showExposureModal, setShowExposureModal] = useState(false)
  const [revisionNotice, setRevisionNotice] = useState(null)

  // ======= For transaction sub-panel =======
  const [transactions, setTransactions] = useState([])
  const [txDelta, setTxDelta] = useState("")
  const [txReason, setTxReason] = useState("")

  // ======= Error/loading state =======
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)

  // ======= Low Stock Alerts =======
  const [consumableShortages, setConsumableShortages] = useState([])
  const [selectedShortageIds, setSelectedShortageIds] = useState([])

  // ======= Multi-vendor support =======
  const [consumableVendors, setConsumableVendors] = useState([]) 
  const [vendors, setVendors] = useState([])

  // ======= Image handling =======
  const [selectedFile, setSelectedFile] = useState(null);
  const [imageUrl, setImageUrl] = useState("");
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  // ======= Vendor editing =======
  const [newVendorId, setNewVendorId] = useState("")
  const [newVendorCost, setNewVendorCost] = useState("")
  const [newVendorSKU, setNewVendorSKU] = useState("")
  const [editingVendorId, setEditingVendorId] = useState(null)
  const [editCost, setEditCost] = useState("")
  const [editSKU, setEditSKU] = useState("")

  // ======= Laser Specs State =======
  const [laserSpecs, setLaserSpecs] = useState([]);
  const [specsLoading, setSpecsLoading] = useState(false);
  const [materialTypes, setMaterialTypes] = useState([]); 

  // ======= Navigation & Config =======
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const API_BASE = config.API_BASE_URL;

  // ======= Labels (4x6 with a cut-out 3" x 3/4" bin insert) =======
  const [showLabelsModal, setShowLabelsModal] = useState(false)
  const [labelBusy, setLabelBusy] = useState(false)
  
  // ======= Active Tab State =======
  const [activeTab, setActiveTab] = useState('details'); // 'details', 'laser', 'analytics'

  // ==========================================
  // UTILITIES
  // ==========================================

  function formatNumericValue(val) {
    const num = Number(val) || 0
    if (num < 0) {
      return <span style={{ color: "red", fontWeight: "bold" }}>{num}</span>
    }
    return num
  }

  // ==========================================
  // INITIAL DATA LOADING
  // ==========================================
  useEffect(() => {
    loadConsumables()
    loadVendors()
    loadConsumableShortages()
    loadLaserSpecs()
    loadMaterialTypes()
    loadCategories()
  }, [])

  useEffect(() => {
    loadConsumables();
  }, [filterType]);

  // ?id=71 opens that item: the QR code on its printed label links here. The
  // parameter is dropped once read, so the address bar never names an item the
  // user has since moved off (a reload would reopen the label's item).
  useEffect(() => {
    if (!searchParams.has('id')) return
    const id = Number(searchParams.get('id'))
    if (Number.isInteger(id) && id > 0) handleSelectConsumable({ consumable_id: id })
    setSearchParams({}, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  async function handlePrintLabel() {
    if (!dbConsumableId) return
    setLabelBusy(true)
    try {
      await fetchConsumableLabels(API_BASE, [dbConsumableId], 1, 'print')
    } catch (err) {
      alert(err.message || 'Could not make the label.')
    } finally {
      setLabelBusy(false)
    }
  }

  async function loadConsumables() {
    setLoading(true)
    setError(null)
    try {
      setConsumables(await fetchConsumables(filterType))
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  async function loadVendors() {
    try {
      const r = await fetch(`${API_BASE}/vendors`)
      if (!r.ok) throw new Error(`Failed to load vendors: ${r.status}`)
      const data = await r.json()
      setVendors(data)
    } catch (err) {
      console.error("Failed to load vendors:", err)
    }
  }

  async function loadConsumableShortages() {
    try {
      setConsumableShortages(await loadReorderAlerts())
    } catch (err) {
      console.error("Failed to load consumable shortages:", err)
    }
  }

  async function loadLaserSpecs() {
    setSpecsLoading(true);
    try {
      const r = await fetch(`${API_BASE}/laser_specs`);
      if(r.ok) {
          setLaserSpecs(await r.json());
      }
    } catch(err) { 
        console.error(err); 
    } finally { 
        setSpecsLoading(false); 
    }
  }

  async function loadMaterialTypes() {
    try {
        const r = await fetch(`${API_BASE}/materials/types`);
        if(r.ok) {
            const types = await r.json();
            setMaterialTypes(types.map(t => t.type_name).sort());
        }
    } catch(err) { console.error(err); }
  }

  async function loadConsumableVendors(consumableId) {
    try {
      const r = await fetch(`${API_BASE}/consumables/${consumableId}/vendors`)
      if (!r.ok) throw new Error(`Failed to load vendors for consumable #${consumableId}`)
      const data = await r.json()
      setConsumableVendors(data)
    } catch (err) {
      console.error("Failed to load consumable vendors:", err)
      setConsumableVendors([])
    }
  }

  async function loadCategories() {
    try {
      const res = await fetch(`${API_BASE}/consumable_categories`);
      if (res.ok) {
        setCategories(await res.json());
      }
    } catch (err) {
      console.warn("Failed to load categories:", err);
    }
  }

  async function handleAddCategory() {
    const name = newCategoryName.trim();
    if (!name) return;
    try {
      const res = await fetch(`${API_BASE}/consumable_categories`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name })
      });
      if (!res.ok) {
        throw new Error(await apiErrorMessage(res));
      }
      setNewCategoryName("");
      loadCategories();
    } catch (err) {
      alert("Error adding category: " + err.message);
    }
  }

  async function handleDeleteCategory(categoryId, categoryName) {
    if (!window.confirm(`Delete category "${categoryName}"? Existing consumables with this category will keep their value.`)) return;
    try {
      const res = await fetch(`${API_BASE}/consumable_categories/${categoryId}`, { method: "DELETE" });
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      loadCategories();
    } catch (err) {
      alert("Error deleting category: " + err.message);
    }
  }

  // ==========================================
  // FILTERING
  // ==========================================
  const filteredConsumables = consumables.filter(c => {
    const term = searchTerm.toLowerCase();
    const typeMatch = filterType === 'all' || c.consumable_type === filterType;
    // '' is a real state here, not an accident: 14 of the 19 components carry a
    // blank category, so an exact-match-only filter made picking ANY category
    // hide most of them.
    const catMatch = categoryFilter === 'All'
      || (categoryFilter === '__NONE__' ? !(c.category || '').trim() : (c.category || '') === categoryFilter);
    const searchMatch = matchesConsumableSearch(c, term);
    return typeMatch && catMatch && searchMatch;
  });

  // The Labels dialog lists the sidebar as filtered, plus the open item when a
  // filter hides it (opened from a label's QR code with a category picked), so
  // the item it pre-ticks is always one it shows.
  const labelItems = (dbConsumableId && !filteredConsumables.some(c => c.consumable_id === dbConsumableId))
    ? [
        consumables.find(c => c.consumable_id === dbConsumableId)
          || { consumable_id: dbConsumableId, product_id: productId, description, category },
        ...filteredConsumables,
      ]
    : filteredConsumables

  // ==========================================
  // CONSUMABLE FORM HANDLERS
  // ==========================================
  function handleCreateNew() {
    setSelectedConsumable(null)
    setDbConsumableId(null)
    setProductId("")
    setDescription("")
    setStockQuantity("")
    setReorderPoint("")
    setCategory("")
    setNotes("")
    setConsumableType("consumable")
    setPrice("")
    setWeightLbs("")
    setMinOrderQty("1")
    setOrderInQtyOf("1")
    setImageUrl("")
    setSelectedFile(null)
    setImagePreviewUrl(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }

    setTransactions([])
    setTxDelta("")
    setTxReason("")
    setConsumableVendors([])
    setEditingVendorId(null)
    setNewVendorSKU("")
    setActiveTab('details') 
  }

  async function handleSelectConsumable(cons) {
    setSelectedConsumable(cons)
    setError(null)
    try {
      const detail = await loadConsumable(cons.consumable_id)
      fillForm(detail)
      loadConsumableVendors(cons.consumable_id)
    } catch (err) {
      setError(err.message)
    }
  }

  function fillForm(cons) {
    setDbConsumableId(cons.consumable_id)
    setProductId(cons.product_id || "")
    setDescription(cons.description || "")
    setStockQuantity(String(cons.stock_quantity || ""))
    setReorderPoint(String(cons.reorder_point || ""))
    setCategory(cons.category || "")
    setNotes(cons.notes || "")
    setConsumableType(cons.consumable_type || "consumable")
    setPrice(cons.price || "")
    setWeightLbs(cons.weight_lbs || "")
    setMinOrderQty(String(cons.min_order_qty ?? "1"))
    setOrderInQtyOf(String(cons.order_in_qty_of ?? "1"))
    setImageUrl(cons.image_url || "");
    setSelectedFile(null);
    setImagePreviewUrl(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }

    loadConsumableTransactions(cons.consumable_id)
    setTxDelta("")
    setTxReason("")
    setEditingVendorId(null)
    setEditSKU("")

    // Load "Produced By" part linkage for component consumables
    setProducedByPart(null)
    setComponentDetail(null)
    setComponentDocs([])
    setRevisionNotice(null)
    if (cons.consumable_type === 'component') {
      fetch(`${API_BASE}/consumables/${cons.consumable_id}/produced-by`)
        .then(res => res.ok ? res.json() : null)
        .then(data => setProducedByPart(data))
        .catch(() => setProducedByPart(null))
      loadComponentDetail(cons.consumable_id)
    }
  }

  // Resolves spec_owner, the governing part for a shadow-twin, and the revision
  // in force. Everything revision-related on this page keys off it, so a plain
  // consumable never sees any of it.
  async function loadComponentDetail(consumableId) {
    try {
      const res = await fetch(`${API_BASE}/components/${consumableId}`)
      if (!res.ok) { setComponentDetail(null); return }
      setComponentDetail(await res.json())
    } catch {
      setComponentDetail(null)
    }
    try {
      const dres = await fetch(`${API_BASE}/components/${consumableId}/documents`)
      if (dres.ok) {
        const body = await dres.json()
        setComponentDocs(body.documents || [])
      }
    } catch {
      setComponentDocs([])
    }
  }

  function refreshComponent() {
    if (dbConsumableId) loadComponentDetail(dbConsumableId)
    setComponentRefreshKey(k => k + 1)
  }

  async function loadConsumableTransactions(consumableId) {
    const data = await fetchConsumableTransactions(consumableId)
    if (data) setTransactions(data)
  }

  async function handleSaveConsumable() {
    setError(null);
    if (!productId) {
      alert("Consumable Product ID (SKU) is required.");
      return;
    }
    if (consumableType === 'component' && (!weightLbs || parseFloat(weightLbs) <= 0)) {
      alert("Weight (lbs) is required for components.");
      return;
    }

    let finalImageUrl = imageUrl;

    if (selectedFile) {
        try {
            const presignedUrlResponse = await fetch(`${API_BASE}/v1/s3/presigned-url?file_name=${encodeURIComponent(selectedFile.name)}&file_type=${encodeURIComponent(selectedFile.type)}`, {
              method: 'POST'
            });
            if (!presignedUrlResponse.ok) throw new Error("Failed to get presigned URL.");
            const presignedUrlData = await presignedUrlResponse.json();
            const formData = new FormData();
            Object.keys(presignedUrlData.fields).forEach(key => {
                formData.append(key, presignedUrlData.fields[key]);
            });
            formData.append('file', selectedFile);
            const s3UploadResponse = await fetch(presignedUrlData.url, {
                method: 'POST',
                body: formData,
            });
            if (!s3UploadResponse.ok) throw new Error("Failed to upload image to S3.");
            finalImageUrl = `${presignedUrlData.url}${presignedUrlData.fields.key}`;
        } catch (uploadError) {
            setError(uploadError.message);
            return;
        }
    }

    let url = `${API_BASE}/consumables`;
    let method = "POST";
    if (dbConsumableId) {
        url = `${API_BASE}/consumables/${dbConsumableId}`;
        method = "PATCH";
    }

    // A component's spec edits are recorded field-by-field with a name against
    // them, so the PIN is what makes the audit worth having. Cancelling the
    // prompt aborts the save rather than saving unattributed.
    let pin = null;
    if (consumableType === 'component') {
        pin = await pinPrompt("PIN to save this component:");
        if (!pin) return;
    }

    const bodyToSend = {
        pin,
        product_id: productId,
        description,
        category,
        notes,
        consumable_type: consumableType,
        reorder_point: parseInt(reorderPoint, 10) || 0,
        image_url: finalImageUrl,
        price: parseFloat(price) || 0,
        weight_lbs: weightLbs ? parseFloat(weightLbs) : null,
        min_order_qty: parseInt(minOrderQty, 10) || 1,
        order_in_qty_of: parseInt(orderInQtyOf, 10) || 1
    };

    try {
        const res = await fetch(url, {
            method,
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(bodyToSend)
        });

        if (!res.ok) {
            let detail = await res.text();
            try { detail = detailToString(JSON.parse(detail).detail) || detail; } catch { /* plain text */ }
            throw new Error(detail);
        }

        const payload = await res.json().catch(() => ({}));

        if (method === "POST") {
            // Was: handleCreateNew(), which wiped the form and stranded the
            // operator on the item they had just created. Select it instead —
            // creating a component is the moment you want to attach its drawing.
            alert(
              `New item created! ID = ${payload.consumable_id}`
              + (payload.revision_code ? ` (Rev ${payload.revision_code})` : "")
            );
            await loadConsumables();
            await handleSelectConsumable({
                consumable_id: payload.consumable_id,
                consumable_type: consumableType,
            });
        } else {
            // Was: a bare {consumable_id} stub, so anything reading off
            // selectedConsumable saw undefined until the detail fetch landed.
            const changed = payload.changed_fields || [];
            alert(
              changed.length
                ? `Item #${dbConsumableId} updated — ${changed.join(", ")}.`
                : `Item #${dbConsumableId}: no changes to save.`
            );
            await handleSelectConsumable({
                consumable_id: dbConsumableId,
                consumable_type: consumableType,
            });
        }

        loadConsumables();
        loadConsumableShortages();

    } catch (err) {
        setError(err.message);
    }
  }

  async function handleDeleteConsumable() {
    if (!dbConsumableId) {
      alert("No item selected.")
      return
    }
    if (!window.confirm("Really delete this item?")) return
    // Deleting a consumable cascades its stock ledger and its assembly BOM
    // lines. The API refuses when any of that exists, but a destructive action
    // should carry a name regardless.
    const pin = await pinPrompt("PIN to delete this item:")
    if (!pin) return
    try {
      const res = await fetch(
        `${API_BASE}/consumables/${dbConsumableId}?pin=${encodeURIComponent(pin)}`,
        { method: "DELETE" }
      )
      if (!res.ok) {
        const raw = await res.text()
        let detail = raw
        try {
          const body = JSON.parse(raw)
          if (body && body.detail) detail = body.detail
        } catch (_) {}
        if (res.status === 409) {
          alert("Cannot delete this item:\n\n" + detail)
          return
        }
        throw new Error(detail)
      }
      alert("Item deleted.")
      loadConsumables()
      loadConsumableShortages()
      handleCreateNew()
    } catch (err) {
      setError(err.message)
    }
  }

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePaste = (e) => {
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf("image") !== -1) {
        const file = items[i].getAsFile();
        setSelectedFile(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreviewUrl(reader.result);
        };
        reader.readAsDataURL(file);
        break;
      }
    }
  };

  // ==========================================
  // TRANSACTIONS & PO LOGIC
  // ==========================================
  async function handleAddTransaction() {
    // A count correction sends the COUNTED quantity (see recordConsumableTransaction).
    const { data } = await recordConsumableTransaction({
      consumableId: dbConsumableId, reason: txReason, quantity: txDelta, ui: desktopUi,
    })
    if (!data) return
    setStockQuantity(String(data.new_quantity))
    loadConsumableTransactions(dbConsumableId)
    loadConsumableShortages()
    setTxDelta("")
    setTxReason("")
  }

  function handleToggleShortage(id, checked) {
    if (checked) {
      setSelectedShortageIds(prev => [...prev, id])
    } else {
      setSelectedShortageIds(prev => prev.filter(x => x !== id))
    }
  }

  async function handleGeneratePOFromShortages() {
    const selected = consumableShortages.filter(c =>
      selectedShortageIds.includes(c.consumable_id)
    )
    if (!selected.length) {
      alert("No items selected!")
      return
    }

    const vendorGroups = {}
    const skippedNoVendor = []
    for (let cons of selected) {
      // recommended_qty comes from the API (reorder_alert_row), already net of
      // reserved AND inbound and already rounded to MOQ / order multiple. The
      // old client-side `reorder_point - stock_quantity` ignored both, so it
      // proposed buying back to the floor on an item with far deeper committed
      // demand — KC0168 said 84 against a real need of 253.
      const quantityNeeded = cons.recommended_qty
      if (!quantityNeeded || quantityNeeded <= 0) {
        continue
      }
      if (!cons.vendor_id) {
        skippedNoVendor.push(cons.product_id)
        continue
      }
      const vId = cons.vendor_id
      if (!vendorGroups[vId]) {
        vendorGroups[vId] = []
      }
      vendorGroups[vId].push({
        item_type: "Consumable",
        consumable_id: cons.consumable_id,
        quantity_ordered: quantityNeeded,
        cost_per_unit: 0
      })
    }

    if (!Object.keys(vendorGroups).length) {
      alert(
        "None of the selected items had a known vendor." +
        (skippedNoVendor.length
          ? `\n\nAdd a vendor on: ${skippedNoVendor.join(", ")}`
          : "")
      )
      return
    }
    if (skippedNoVendor.length) {
      alert(
        "These items are short but have no vendor, so they were left off the PO:\n" +
        `${skippedNoVendor.join(", ")}\n\nAdd a vendor to each, then re-run.`
      )
    }

    const createdPOs = []
    const todayStr = shopToday() // the shop's (Central) day: UTC's is tomorrow after 7 PM CT

    for (const [vendorId, lines] of Object.entries(vendorGroups)) {
      let vendorName = ""
      try {
        const vr = await fetch(`${API_BASE}/vendors/${vendorId}`)
        if (vr.ok) {
          const vd = await vr.json()
          vendorName = vd.name
        }
      } catch (err) {
        console.error("Could not fetch vendor name:", err)
      }

      const payload = {
        supplier: vendorName,
        order_date: todayStr,
        estimated_receive_date: null,
        notes: "Auto-created from reorder alerts",
        lines
      }

      try {
        const res = await fetch(`${API_BASE}/purchase_orders`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        })
        if (!res.ok) {
          throw new Error(await apiErrorMessage(res, `Failed to create PO for vendor #${vendorId}`));
        }
        const data = await res.json()
        createdPOs.push(data)
      } catch (err) {
        alert(err.message)
        return
      }
    }

    alert(
      `Successfully created ${createdPOs.length} PO(s)!\n` +
      createdPOs.map(p => `PO# ${p.po_number}`).join(", ")
    )
    loadConsumableShortages()
    setSelectedShortageIds([])
  }

  // ==========================================
  // VENDOR TABLE LOGIC
  // ==========================================
  async function handleAddConsumableVendor() {
    if (!dbConsumableId) {
      alert("No item selected.")
      return
    }
    if (!newVendorId) {
      alert("Select a vendor.")
      return
    }
    const payload = {
      vendor_id: parseInt(newVendorId, 10),
      cost: parseFloat(newVendorCost) || 0,
      vendor_sku: newVendorSKU
    }
    try {
      const r = await fetch(`${API_BASE}/consumables/${dbConsumableId}/vendors`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      })
      if (!r.ok) {
        throw new Error(await apiErrorMessage(r));
      }
      alert("Supplier added!")
      setNewVendorId("")
      setNewVendorCost("")
      setNewVendorSKU("")
      loadConsumableVendors(dbConsumableId)
    } catch (err) {
      alert("Failed to add supplier: " + err.message)
    }
  }

  async function handleRemoveConsumableVendor(cvId) {
    if (!window.confirm("Remove this supplier from the item?")) return
    try {
      const r = await fetch(`${API_BASE}/consumables/${dbConsumableId}/vendors/${cvId}`, {
        method: "DELETE"
      })
      if (!r.ok) {
        throw new Error(await apiErrorMessage(r));
      }
      alert("Supplier association removed.")
      loadConsumableVendors(dbConsumableId)
    } catch (err) {
      alert("Failed to remove association: " + err.message)
    }
  }

  function handleEditVendorRow(cv) {
    setEditingVendorId(cv.consumable_vendor_id)
    setEditCost(String(cv.cost))
    setEditSKU(cv.vendor_sku || "")
  }

  function handleCancelEdit() {
    setEditingVendorId(null)
    setEditCost("")
    setEditSKU("")
  }

  async function handleUpdateConsumableVendor(cvId) {
    if (!dbConsumableId) return
    const payload = {
      cost: parseFloat(editCost) || 0,
      vendor_sku: editSKU
    }
    try {
      const r = await fetch(`${API_BASE}/consumables/${dbConsumableId}/vendors/${cvId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      })
      if (!r.ok) {
        throw new Error(await apiErrorMessage(r));
      }
      alert("Supplier updated!")
      setEditingVendorId(null)
      setEditCost("")
      setEditSKU("")
      loadConsumableVendors(dbConsumableId)
    } catch (err) {
      alert("Failed to update supplier: " + err.message)
    }
  }

  // --- NEW: Set Preferred Vendor ---
  async function handleSetPreferred(consumableVendorId) {
    if (!dbConsumableId) return
    if (!window.confirm("Set this vendor as the preferred supplier for this item?")) return

    try {
        const res = await fetch(`${API_BASE}/consumables/${dbConsumableId}/vendors/${consumableVendorId}/set-preferred`, {
            method: "POST"
        });
        if (!res.ok) {
            throw new Error(await apiErrorMessage(res));
        }
        alert("Preferred vendor updated.");
        loadConsumableVendors(dbConsumableId);
    } catch (err) {
        alert("Error setting preferred vendor: " + err.message);
    }
  }

  function goToPurchaseOrders() {
    navigate("/purchase-orders")
  }

  // ==========================================
// INTERNAL COMPONENT: LASER SPECS MANAGER
// ==========================================
function LaserSpecsManager() {
    // 1. Add state to track which ID we are editing (null = create mode)
    const [editingSpecId, setEditingSpecId] = useState(null);
    const [thicknesses, setThicknesses] = useState([]); // Store all thickness options

    const [newSpec, setNewSpec] = useState({
        material_type: "",
        thickness_id: "", // Now using ID instead of min/max range
        gas_type: "N2",
        gas_flow_cfh: "",
        gas_pressure_psi: "",
        feedrate_ipm: "",
        pierce_time_sec: "",
        nozzle_consumable_id: "",
        nozzle_name: ""
    });

    useEffect(() => {
        loadThicknessOptions();
    }, []);

    async function loadThicknessOptions() {
        try {
            const res = await fetch(`${API_BASE}/material_thicknesses`);
            if (res.ok) {
                setThicknesses(await res.json());
            }
        } catch (err) {
            console.error("Failed to load thicknesses", err);
        }
    }

    // Filter list for potential nozzles
    const nozzleOptions = useMemo(() => {
        return consumables.filter(c => 
            (c.description || "").toLowerCase().includes("nozzle") || 
            (c.category || "").toLowerCase().includes("nozzle") ||
            (c.category || "").toLowerCase().includes("laser")
        );
    }, [consumables]);

    // Filter thicknesses based on selected material_type
    const availableThicknesses = useMemo(() => {
        if (!newSpec.material_type) return [];
        return thicknesses.filter(t => t.material_type === newSpec.material_type);
    }, [thicknesses, newSpec.material_type]);

    // 2. Logic to populate form when "Edit" is clicked
    const handleEditClick = (spec) => {
        setEditingSpecId(spec.spec_id);
        setNewSpec({
            material_type: spec.material_type || "",
            thickness_id: spec.thickness_id ? spec.thickness_id.toString() : "",
            gas_type: spec.gas_type || "N2",
            gas_flow_cfh: spec.gas_flow_cfh || "",
            gas_pressure_psi: spec.gas_pressure_psi || "",
            feedrate_ipm: spec.feedrate_ipm || "",
            pierce_time_sec: spec.pierce_time_sec || "",
            nozzle_consumable_id: spec.nozzle_consumable_id ? spec.nozzle_consumable_id.toString() : "",
            nozzle_name: spec.nozzle_name || ""
        });
    };

    // 3. Logic to cancel edit and reset form
    const handleCancelEdit = () => {
        setEditingSpecId(null);
        setNewSpec({
            material_type: "", 
            thickness_id: "",
            gas_type: "N2", 
            gas_flow_cfh: "", 
            gas_pressure_psi: "",
            feedrate_ipm: "", 
            pierce_time_sec: "", 
            nozzle_consumable_id: "", 
            nozzle_name: ""
        });
    };

    const handleSpecSave = async () => {
        if (!newSpec.material_type || !newSpec.thickness_id) {
            alert("Please select both a Material Type and a specific Thickness.");
            return;
        }

        const payload = {
            ...newSpec,
            thickness_id: parseInt(newSpec.thickness_id, 10),
            gas_flow_cfh: parseFloat(newSpec.gas_flow_cfh) || 0,
            gas_pressure_psi: parseFloat(newSpec.gas_pressure_psi) || 0,
            feedrate_ipm: parseFloat(newSpec.feedrate_ipm) || 0,
            pierce_time_sec: parseFloat(newSpec.pierce_time_sec) || 0,
            nozzle_consumable_id: newSpec.nozzle_consumable_id ? parseInt(newSpec.nozzle_consumable_id) : null
        };

        try {
            let url = `${API_BASE}/laser_specs`;
            let method = "POST";

            if (editingSpecId) {
                url = `${API_BASE}/laser_specs/${editingSpecId}`;
                method = "PATCH";
            }

            const res = await fetch(url, {
                method: method,
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
            });
            
            if(!res.ok) {
                const msg = await res.text();
                // Check specifically for constraint violation
                if(res.status === 409) {
                    throw new Error("A spec already exists for this Material + Thickness + Gas combination.");
                }
                throw new Error(msg);
            }
            
            loadLaserSpecs(); // Refresh parent state
            handleCancelEdit(); // Reset form and mode
            
        } catch(e) { alert(e.message); }
    };

    const handleSpecDelete = async (id) => {
        if(!window.confirm("Delete this spec?")) return;
        try {
            await fetch(`${API_BASE}/laser_specs/${id}`, { method: "DELETE" });
            
            if (editingSpecId === id) {
                handleCancelEdit();
            }
            
            loadLaserSpecs();
        } catch(e) { alert(e.message); }
    };

    return (
        <div>
            <div className={`card mb-3 ${editingSpecId ? 'border-primary bg-white' : 'bg-light'}`}>
                <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0">
                            {editingSpecId ? `Editing Spec #${editingSpecId}` : "Add New Laser Tech Spec"}
                        </h6>
                        {editingSpecId && (
                            <button className="btn btn-xs btn-secondary" onClick={handleCancelEdit}>
                                Cancel Edit
                            </button>
                        )}
                    </div>
                    
                    <div className="row g-2 align-items-end">
                        <div className="col-md-2">
                            <label className="form-label small">Material</label>
                            <select className="form-select form-select-sm" 
                                value={newSpec.material_type} 
                                onChange={e => {
                                    // Reset thickness ID when material type changes to prevent invalid selection
                                    setNewSpec({...newSpec, material_type: e.target.value, thickness_id: ""});
                                }}>
                                <option value="">Select...</option>
                                {materialTypes.map((t, idx) => <option key={idx} value={t}>{t}</option>)}
                            </select>
                        </div>
                        <div className="col-md-2">
                            <label className="form-label small">Thickness</label>
                            <select className="form-select form-select-sm" 
                                value={newSpec.thickness_id} 
                                disabled={!newSpec.material_type}
                                onChange={e => setNewSpec({...newSpec, thickness_id: e.target.value})}>
                                <option value="">Select...</option>
                                {availableThicknesses.map((t) => (
                                    <option key={t.thickness_id} value={t.thickness_id}>
                                        {t.display_thickness} ({t.thickness_in}")
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-1">
                            <label className="form-label small">Gas</label>
                            <select className="form-select form-select-sm" value={newSpec.gas_type} onChange={e => setNewSpec({...newSpec, gas_type: e.target.value})}>
                                <option value="N2">N2</option><option value="O2">O2</option><option value="Mixed">Mixed</option>
                            </select>
                        </div>
                        <div className="col-md-1">
                            <label className="form-label small">CFH</label>
                            <input className="form-control form-control-sm" type="number" value={newSpec.gas_flow_cfh} onChange={e => setNewSpec({...newSpec, gas_flow_cfh: e.target.value})} />
                        </div>
                        <div className="col-md-1">
                            <label className="form-label small">PSI</label>
                            <input className="form-control form-control-sm" type="number" value={newSpec.gas_pressure_psi} onChange={e => setNewSpec({...newSpec, gas_pressure_psi: e.target.value})} />
                        </div>
                        <div className="col-md-1">
                            <label className="form-label small">IPM</label>
                            <input className="form-control form-control-sm" type="number" value={newSpec.feedrate_ipm} onChange={e => setNewSpec({...newSpec, feedrate_ipm: e.target.value})} />
                        </div>
                        <div className="col-md-1">
                            <label className="form-label small">Pierce (s)</label>
                            <input className="form-control form-control-sm" type="number" step="0.1" value={newSpec.pierce_time_sec} onChange={e => setNewSpec({...newSpec, pierce_time_sec: e.target.value})} />
                        </div>
                        <div className="col-md-1">
                            <label className="form-label small">Nozzle</label>
                            <select className="form-select form-select-sm" 
                                value={newSpec.nozzle_consumable_id} 
                                onChange={e => {
                                    const id = e.target.value;
                                    const obj = nozzleOptions.find(n => n.consumable_id.toString() === id);
                                    setNewSpec({...newSpec, nozzle_consumable_id: id, nozzle_name: obj ? obj.product_id : ""});
                                }}>
                                <option value="">Select Stock...</option>
                                {nozzleOptions.map(n => <option key={n.consumable_id} value={n.consumable_id}>{n.product_id}</option>)}
                            </select>
                        </div>
                        <div className="col-md-2">
                            <button 
                                className={`btn btn-sm w-100 mt-4 ${editingSpecId ? 'btn-success' : 'btn-primary'}`} 
                                onClick={handleSpecSave}
                            >
                                {editingSpecId ? "Update Spec" : "Add Spec"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {specsLoading ? <p>Loading specs...</p> : (
            <table className="table table-sm table-bordered table-hover">
                <thead className="table-light">
                    <tr>
                        <th>Material</th>
                        <th>Thickness</th>
                        <th>Gas</th>
                        <th>Flow (CFH)</th>
                        <th>Press (PSI)</th>
                        <th>Speed (IPM)</th>
                        <th>Pierce (s)</th>
                        <th>Nozzle</th>
                        <th style={{width: '120px'}}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {laserSpecs.map(spec => (
                        <tr key={spec.spec_id} className={editingSpecId === spec.spec_id ? "table-active" : ""}>
                            <td>{spec.material_type}</td>
                            <td>
                                {spec.display_thickness || "?"} 
                                <span className="text-muted small ms-1">({spec.thickness_in}")</span>
                            </td>
                            <td>{spec.gas_type}</td>
                            <td>{spec.gas_flow_cfh}</td>
                            <td>{spec.gas_pressure_psi}</td>
                            <td>{spec.feedrate_ipm}</td>
                            <td>{spec.pierce_time_sec}</td>
                            <td>{spec.nozzle_name} {spec.nozzle_product_id && <span className="badge bg-secondary">{spec.nozzle_product_id}</span>}</td>
                            <td>
                                <button 
                                    className="btn btn-sm btn-outline-primary me-2" 
                                    onClick={() => handleEditClick(spec)}
                                    title="Edit Spec"
                                >
                                    ✎
                                </button>
                                <button 
                                    className="btn btn-sm btn-outline-danger" 
                                    onClick={() => handleSpecDelete(spec.spec_id)}
                                    title="Delete Spec"
                                >
                                    X
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            )}
        </div>
    );
}

  // ==========================================
  // MAIN RENDER
  // ==========================================
  return (
    <div className="d-flex" style={{ height: "100vh" }}>
      {/* LEFT SIDEBAR */}
      <div className="border-end p-3" style={{ width: "300px", flexShrink: 0, overflowY: "auto" }}>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0">Inventory</h5>
          <div className="d-flex gap-1">
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setShowLabelsModal(true)}
              title="Print 4x6 labels with a cut-out bin insert for the items in this list"
            >
              Labels
            </button>
            <button className="btn btn-sm btn-outline-primary" onClick={handleCreateNew}>
              New
            </button>
          </div>
        </div>
        
        <div className="mb-2">
          <div className="form-check form-check-inline">
            <input className="form-check-input" type="radio" name="filterType" id="filterAll" value="all" checked={filterType === 'all'} onChange={(e) => setFilterType(e.target.value)} />
            <label className="form-check-label" htmlFor="filterAll">All</label>
          </div>
          <div className="form-check form-check-inline">
            <input className="form-check-input" type="radio" name="filterType" id="filterConsumable" value="consumable" checked={filterType === 'consumable'} onChange={(e) => setFilterType(e.target.value)} />
            <label className="form-check-label" htmlFor="filterConsumable">Consumables</label>
          </div>
          <div className="form-check form-check-inline">
            <input className="form-check-input" type="radio" name="filterType" id="filterComponent" value="component" checked={filterType === 'component'} onChange={(e) => setFilterType(e.target.value)} />
            <label className="form-check-label" htmlFor="filterComponent">Components</label>
          </div>
        </div>

        <select
          className="form-select form-select-sm mb-2"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
        >
          <option value="All">All Categories</option>
          {/* 14 of the 19 components have a blank category, so without this
              option picking any category made most of them disappear. */}
          <option value="__NONE__">(uncategorized)</option>
          {categories.map(c => (
            <option key={c.category_id} value={c.name}>{c.name}</option>
          ))}
        </select>

        <input
          type="text"
          className="form-control mb-2"
          placeholder="Search by Product ID, Description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {loading && <div>Loading...</div>}
        {!loading &&
          filteredConsumables.map((c) => (
            <div
              key={c.consumable_id}
              className={`p-2 border-bottom ${
                selectedConsumable && selectedConsumable.consumable_id === c.consumable_id
                  ? "bg-primary-subtle"
                  : "bg-white"
              }`}
              style={{ cursor: "pointer" }}
              onClick={() => handleSelectConsumable(c)}
            >
              <div className="fw-bold">
                {c.product_id || "???"}
                {c.consumable_type === 'component' && c.current_revision_code && (
                  <span className="badge bg-dark ms-2" style={{ fontSize: "0.65rem" }}>
                    Rev {c.current_revision_code}
                  </span>
                )}
              </div>
              <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                {c.description}
              </div>
              <div style={{ fontSize: "0.8rem", fontStyle: "italic" }}>
                Category: {c.category || "-"}
              </div>
              <div style={{ fontSize: "0.8rem" }}>
                Qty: {formatNumericValue(c.stock_quantity)} | ROP:{" "}
                {formatNumericValue(c.reorder_point)}
              </div>
            </div>
          ))}
      </div>

      {/* RIGHT CONTENT AREA */}
      <div className="flex-grow-1 p-4 overflow-auto">
        {error && <div className="alert alert-danger">{error}</div>}

        {/* NAVIGATION TABS */}
        <ul className="nav nav-tabs mb-3">
            <li className="nav-item">
                <button 
                    className={`nav-link ${activeTab === 'details' ? 'active' : ''}`} 
                    onClick={() => setActiveTab('details')}
                >
                    Item Details
                </button>
            </li>
            <li className="nav-item">
                <button 
                    className={`nav-link ${activeTab === 'laser' ? 'active' : ''}`} 
                    onClick={() => setActiveTab('laser')}
                >
                    Laser Tech Specs
                </button>
            </li>
            <li className="nav-item">
                <button
                    className={`nav-link ${activeTab === 'analytics' ? 'active' : ''}`}
                    onClick={() => setActiveTab('analytics')}
                >
                    Laser Analytics
                </button>
            </li>
            <li className="nav-item">
                <button
                    className={`nav-link ${activeTab === 'gas_sessions' ? 'active' : ''}`}
                    onClick={() => setActiveTab('gas_sessions')}
                >
                    Gas Sessions
                </button>
            </li>
            <li className="nav-item">
                <button
                    className={`nav-link ${activeTab === 'settings' ? 'active' : ''}`}
                    onClick={() => setActiveTab('settings')}
                >
                    Settings
                </button>
            </li>
        </ul>

        {/* TAB CONTENT: ITEM DETAILS */}
        {activeTab === 'details' && (
        <>
            <div className="card mb-3">
              <div className="card-header">
                Low Stock Alerts
                <span className="text-muted small ms-2">
                  available = on hand + inbound &minus; reserved
                </span>
              </div>
              <div className="card-body">
                {consumableShortages.length === 0 ? (
                  <p>Nothing needs ordering right now.</p>
                ) : (
                  <ul style={{ listStyle: "none", paddingLeft: 0 }}>
                    {consumableShortages.map((c) => (
                      <li key={c.consumable_id} style={{ marginBottom: "0.5rem" }}>
                        <label>
                          <input
                            type="checkbox"
                            checked={selectedShortageIds.includes(c.consumable_id)}
                            onChange={(e) =>
                              handleToggleShortage(c.consumable_id, e.target.checked)
                            }
                            style={{ marginRight: "0.5rem" }}
                          />
                          <strong>{c.product_id}</strong> - {c.description}
                          {" ("}
                          On hand={formatNumericValue(c.stock_quantity)}
                          {Number(c.reserved_quantity) > 0 &&
                            <>, Reserved={formatNumericValue(c.reserved_quantity)}</>}
                          {Number(c.inbound_qty) > 0 &&
                            <>, Inbound={formatNumericValue(c.inbound_qty)}</>}
                          , Available={formatNumericValue(c.available)}
                          {Number(c.reorder_point) > 0 &&
                            <>, ROP={formatNumericValue(c.reorder_point)}</>}
                          {")"}
                          {" "}
                          <span className="badge bg-danger">
                            Order {c.recommended_qty}
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
                {consumableShortages.length > 0 && (
                  <button
                    className="btn btn-sm btn-primary mt-2"
                    onClick={handleGeneratePOFromShortages}
                  >
                    Create PO from Selected
                  </button>
                )}
              </div>
            </div>

            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4 className="d-flex align-items-center mb-0">
                {dbConsumableId
                  ? `Editing Item (#${dbConsumableId})`
                  : "Create New Item"}
                {consumableType === 'component' && componentDetail && (
                  <ComponentRevisionBadge
                    component={componentDetail}
                    busy={false}
                    onNewRevision={() => setShowRevisionModal(true)}
                  />
                )}
              </h4>
              <div className="d-flex gap-2">
                {consumableType === 'component' && componentDetail
                  && !componentDetail.spec_read_only && componentDetail.current_revision_id && (
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => setShowExposureModal(true)}
                    title="Which open POs, sales orders, quotes and BOM rows are still on an older revision"
                  >
                    Open orders on old revisions
                  </button>
                )}
                {dbConsumableId && (
                  <button
                    className="btn btn-sm btn-outline-dark"
                    onClick={handlePrintLabel}
                    disabled={labelBusy}
                    title="4x6 label with a 3&quot; x 3/4&quot; bin insert to cut out. Prints the item as last saved."
                  >
                    {labelBusy ? "Making label..." : "Print Label"}
                  </button>
                )}
                <button className="btn btn-sm btn-secondary" onClick={goToPurchaseOrders}>
                  Go to Purchase Orders
                </button>
              </div>
            </div>

            {revisionNotice && (
              <div className="alert alert-success alert-dismissible py-2">
                {revisionNotice}
                <button type="button" className="btn-close" onClick={() => setRevisionNotice(null)} />
              </div>
            )}

            <div className="row">
                {/* Left Column for Fields */}
                <div className="col-md-8">
                    <div className="card mb-4">
                        <h5 className="card-header">Item Details</h5>
                        <div className="card-body">
                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <label className="form-label">Product ID (SKU)</label>
                                    <input
                                    type="text"
                                    className="form-control"
                                    value={productId}
                                    onChange={(e) => setProductId(e.target.value)}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">Description</label>
                                    <input
                                    type="text"
                                    className="form-control"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="row mb-3">
                                <div className={consumableType === 'component' ? "col-md-4" : "col-md-6"}>
                                    <label className="form-label">Category</label>
                                    <select
                                    className="form-select"
                                    value={category}
                                    onChange={(e) => setCategory(e.target.value)}
                                    >
                                      <option value="">-- Select Category --</option>
                                      {categories.map(c => (
                                        <option key={c.category_id} value={c.name}>{c.name}</option>
                                      ))}
                                    </select>
                                </div>
                                <div className={consumableType === 'component' ? "col-md-3" : "col-md-4"}>
                                    <label className="form-label">Type</label>
                                    <select
                                      className="form-select"
                                      value={consumableType}
                                      onChange={(e) => setConsumableType(e.target.value)}
                                    >
                                      <option value="consumable">Consumable</option>
                                      <option value="component">Component</option>
                                    </select>
                                </div>

                                {consumableType === 'component' && (
                                    <div className="col-md-3">
                                        <label className="form-label">Book Price ($)</label>
                                        <input
                                        type="number"
                                        step="0.01"
                                        className="form-control"
                                        value={price}
                                        onChange={(e) => setPrice(e.target.value)}
                                        placeholder="0.00"
                                        />
                                    </div>
                                )}

                                {consumableType === 'component' && (
                                <div className="col-md-3">
                                    <label className="form-label">Weight (lbs) <span className="text-danger">*</span></label>
                                    <input
                                    type="number"
                                    step="0.01"
                                    className="form-control"
                                    value={weightLbs}
                                    onChange={(e) => setWeightLbs(e.target.value)}
                                    placeholder="0.00"
                                    style={!weightLbs || parseFloat(weightLbs) <= 0 ? { backgroundColor: '#fff0f0' } : {}}
                                    />
                                </div>
                                )}
                            </div>

                            {consumableType === 'component' && producedByPart && (
                            <div className="row mb-3">
                                <div className="col-md-12">
                                    <label className="form-label">Produced By</label>
                                    <input type="text" className="form-control" readOnly
                                      value={`${producedByPart.part_product_id} - ${producedByPart.part_description || ''}`}
                                      style={{ backgroundColor: '#f0f8ff' }}
                                    />
                                    <small className="text-muted">This component is auto-stocked when the linked part ships.</small>
                                </div>
                            </div>
                            )}

                            <div className="row mb-3">
                                <div className="col-md-12">
                                    <label className="form-label">Notes</label>
                                    <textarea
                                    className="form-control"
                                    rows={2}
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    />
                                </div>
                            </div>

                            {/* Stock Quantity was an editable input that the save
                                path never sent — you typed a number, got "Item
                                updated!", and it reverted on refetch. Stock moves
                                through the Transactions panel below, which writes a
                                ledger row. Shown read-only so the number is still
                                visible where operators expect it.
                                Unit of Measurement was worse: it is not a column on
                                `consumables` at all (it lives on `materials`), so it
                                could never save. Removed. */}
                            <div className="row mb-3">
                                <div className="col-md-4">
                                    <label className="form-label">Stock Quantity</label>
                                    <input
                                    type="number"
                                    className="form-control-plaintext ps-2 border rounded bg-light"
                                    value={stockQuantity || "0"}
                                    readOnly
                                    tabIndex={-1}
                                    />
                                    <div className="form-text">
                                      Changes go through the Transactions panel, which records who
                                      and why.
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">Reorder Point</label>
                                    <input
                                    type="number"
                                    className="form-control"
                                    value={reorderPoint}
                                    onChange={(e) => setReorderPoint(e.target.value)}
                                    />
                                </div>
                                {consumableType === 'component' && componentDetail && (
                                  <div className="col-md-4">
                                      <label className="form-label">Reserved</label>
                                      <input
                                      type="number"
                                      className="form-control-plaintext ps-2 border rounded bg-light"
                                      value={componentDetail.reserved_quantity ?? 0}
                                      readOnly
                                      tabIndex={-1}
                                      />
                                      <div className="form-text">
                                        Committed to open work. A breaking revision is refused while
                                        this is above zero.
                                      </div>
                                  </div>
                                )}
                            </div>

                            <div className="row mb-3">
                                <div className="col-md-4">
                                    <label className="form-label">Min Order Qty</label>
                                    <input
                                    type="number"
                                    className="form-control"
                                    min="1"
                                    value={minOrderQty}
                                    onChange={(e) => setMinOrderQty(e.target.value)}
                                    />
                                    <small className="text-muted">Minimum quantity per order</small>
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">Order in Qty of</label>
                                    <input
                                    type="number"
                                    className="form-control"
                                    min="1"
                                    value={orderInQtyOf}
                                    onChange={(e) => setOrderInQtyOf(e.target.value)}
                                    />
                                    <small className="text-muted">Must order in multiples of this</small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column for Image Upload + Metrics */}
                <div className="col-md-4">
                    <div className="card mb-4">
                        <h5 className="card-header">Image</h5>
                        <div className="card-body text-center">
                            {(imagePreviewUrl || imageUrl) && <img src={imagePreviewUrl || imageUrl} alt="Item" className="img-fluid rounded mb-2" />}
                            <div 
                                onPaste={handlePaste} 
                                className="border rounded p-3 text-center mb-2"
                                style={{ borderStyle: 'dashed' }}
                            >
                                Paste image here or...
                            </div>
                            <input
                                type="file"
                                className="form-control"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                accept="image/*"
                            />
                        </div>
                    </div>

                    {/* Revision + files, for purchased components only. */}
                    {dbConsumableId && consumableType === 'component' && (
                        <>
                            <ComponentFilesCard
                                consumableId={dbConsumableId}
                                productId={productId}
                                onChanged={refreshComponent}
                            />
                            <ComponentRevisionTrail
                                consumableId={dbConsumableId}
                                productId={productId}
                                refreshKey={componentRefreshKey}
                                onChanged={refreshComponent}
                            />
                            <FreeIssueYieldCard
                                consumableId={dbConsumableId}
                                productId={productId}
                                onChanged={refreshComponent}
                            />
                        </>
                    )}

                    {/* Consumable Metrics Widget.
                        ⚠ These three are LASER-WEAR telemetry — Current Life, Avg
                        Life, cost per hour, lifespan trend. They mean something for
                        a nozzle or a gas tank and nothing at all for a steel bar we
                        buy by the piece, where "0 hrs / $0 per hr" is just noise.
                        Gated to consumables; components get the revision and files
                        cards above in the same column instead. */}
                    {dbConsumableId && consumableType !== 'component' && (
                        <>
                            <ConsumableMetrics 
                                consumableId={dbConsumableId} 
                                apiBase={API_BASE} 
                            />
                            {/* Existing Usage Chart */}
                            <ConsumableUsageChart transactions={transactions} />
                            
                            {/* NEW: Lifespan Chart */}
                            <ConsumableLifespanChart 
                                consumableId={dbConsumableId} 
                                apiBase={API_BASE} 
                            />
                        </>
                    )}

                    {/* A component still gets its usage history — that IS meaningful
                        (it is the stock ledger), unlike the tool-life widgets. */}
                    {dbConsumableId && consumableType === 'component' && (
                        <ConsumableUsageChart transactions={transactions} />
                    )}
                </div>
            </div>

            <div className="mb-4">
              <button className="btn btn-primary me-2" onClick={handleSaveConsumable}>
                {dbConsumableId ? "Save Changes" : "Create Item"}
              </button>
              {dbConsumableId && (
                <button className="btn btn-danger" onClick={handleDeleteConsumable}>
                  Delete
                </button>
              )}
            </div>

            {dbConsumableId && (
              <div className="mt-4">
                <h5>Associated Suppliers</h5>
                {consumableVendors.length === 0 ? (
                  <p>No suppliers for this item yet.</p>
                ) : (
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Vendor</th>
                        <th>Vendor SKU</th>
                        <th>Cost/Unit</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {consumableVendors.map((cv, idx) => {
                        const isEditing = editingVendorId === cv.consumable_vendor_id
                        return (
                          <tr key={cv.consumable_vendor_id}>
                            <td>
                              {cv.is_preferred ? (
                                <strong>{cv.vendor_name} (Preferred)</strong>
                              ) : (
                                cv.vendor_name
                              )}
                            </td>
                            <td>
                              {isEditing ? (
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  style={{ width: "150px" }}
                                  value={editSKU}
                                  onChange={(e) => setEditSKU(e.target.value)}
                                />
                              ) : (
                                cv.vendor_sku || ""
                              )}
                            </td>
                            <td>
                              {isEditing ? (
                                <input
                                  type="number"
                                  step="0.01"
                                  className="form-control form-control-sm"
                                  style={{ width: "100px" }}
                                  value={editCost}
                                  onChange={(e) => setEditCost(e.target.value)}
                                />
                              ) : (
                                formatNumericValue(cv.cost)
                              )}
                            </td>
                            <td>
                              {isEditing ? (
                                <>
                                  <button
                                    className="btn btn-sm btn-success me-2"
                                    onClick={() => handleUpdateConsumableVendor(cv.consumable_vendor_id)}
                                  >
                                    Save
                                  </button>
                                  <button
                                    className="btn btn-sm btn-secondary"
                                    onClick={handleCancelEdit}
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <>
                                  {!cv.is_preferred && (
                                    <button 
                                        className="btn btn-sm btn-outline-success me-2"
                                        onClick={() => handleSetPreferred(cv.consumable_vendor_id)}
                                    >
                                        Set Preferred
                                    </button>
                                  )}
                                  <button
                                    className="btn btn-sm btn-primary me-2"
                                    onClick={() => handleEditVendorRow(cv)}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    className="btn btn-sm btn-danger"
                                    onClick={() => handleRemoveConsumableVendor(cv.consumable_vendor_id)}
                                  >
                                    Remove
                                  </button>
                                </>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                )}

                <div className="card mt-3">
                  <div className="card-header">Add New Supplier</div>
                  <div className="card-body d-flex" style={{ gap: "1rem" }}>
                    <div style={{ flex: 1 }}>
                      <label className="form-label">Vendor</label>
                      <select
                        className="form-select"
                        value={newVendorId}
                        onChange={(e) => setNewVendorId(e.target.value)}
                      >
                        <option value="">-- Select a Vendor --</option>
                        {vendors.map((v) => (
                          <option key={v.vendor_id} value={v.vendor_id}>
                            {v.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div style={{ flex: 1 }}>
                      <label className="form-label">Vendor SKU</label>
                      <input
                        type="text"
                        className="form-control"
                        value={newVendorSKU}
                        onChange={(e) => setNewVendorSKU(e.target.value)}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label className="form-label">Cost / Unit</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        value={newVendorCost}
                        onChange={(e) => setNewVendorCost(e.target.value)}
                      />
                    </div>
                    <div style={{ alignSelf: "end" }}>
                      <button className="btn btn-primary" onClick={handleAddConsumableVendor}>
                        Add
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {dbConsumableId && (
              <div className="mt-4">
                <hr />
                <h5>Inventory Transactions</h5>
                <div className="d-flex align-items-center mb-3" style={{ gap: "0.5rem" }}>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <label className="form-label">
                      {txReason === COUNT_CORRECTION_REASON ? "Counted qty" : "Delta"}
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      style={{ width: "100px" }}
                      min={txReason === COUNT_CORRECTION_REASON ? 0 : undefined}
                      placeholder={txReason === COUNT_CORRECTION_REASON ? String(stockQuantity ?? "") : undefined}
                      value={txDelta}
                      onChange={(e) => setTxDelta(e.target.value)}
                    />
                  </div>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <label className="form-label">Reason</label>
                    <select
                      className="form-select"
                      style={{ width: "200px" }}
                      value={txReason}
                      onChange={(e) => setTxReason(e.target.value)}
                    >
                      <option value="">-- Select Reason --</option>
                      {TRANSACTION_REASONS.map((r) => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </div>

                  <div style={{ alignSelf: "end" }}>
                    <button className="btn btn-success ms-2" onClick={handleAddTransaction}>
                      Add Transaction
                    </button>
                  </div>
                </div>

                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Delta</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((t) => (
                      <tr key={t.transaction_id}>
                        <td>{t.created_at}</td>
                        <td>{formatNumericValue(t.delta)}</td>
                        <td>
                          {t.reason}
                          {t.is_count_correction && (
                            <span className="badge bg-secondary ms-2" title="Physical-count correction: not counted as usage or a swap">count</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </>
        )}

        {/* TAB CONTENT: LASER SPECS */}
        {activeTab === 'laser' && <LaserSpecsManager />}

        {/* TAB CONTENT: LASER ANALYTICS */}
        {activeTab === 'analytics' && <LaserAnalytics apiBase={API_BASE} />}

        {/* TAB CONTENT: GAS SESSIONS */}
        {activeTab === 'gas_sessions' && <GasSessionsManager apiBase={API_BASE} />}

        {/* TAB CONTENT: SETTINGS */}
        {activeTab === 'settings' && (
          <div className="row">
            <div className="col-md-6">
              <div className="card">
                <h5 className="card-header">Manage Categories</h5>
                <div className="card-body">
                  <div className="input-group mb-3">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="New category name..."
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleAddCategory(); }}
                    />
                    <button className="btn btn-primary" onClick={handleAddCategory} disabled={!newCategoryName.trim()}>
                      Add
                    </button>
                  </div>
                  {categories.length === 0 ? (
                    <p className="text-muted">No categories defined yet.</p>
                  ) : (
                    <ul className="list-group">
                      {categories.map(c => (
                        <li key={c.category_id} className="list-group-item d-flex justify-content-between align-items-center">
                          {c.name}
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDeleteCategory(c.category_id, c.name)}
                          >
                            Delete
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

      </div>

      {showRevisionModal && componentDetail && (
        <NewComponentRevisionModal
          component={componentDetail}
          documents={componentDocs}
          onClose={() => setShowRevisionModal(false)}
          onCreated={(result) => {
            setShowRevisionModal(false)
            setRevisionNotice(
              `${productId} is now Rev ${result.revision_code}.`
              + (result.open_refs && result.open_refs.eligible
                  ? ` ${result.open_refs.eligible} open reference(s) can be moved onto it — see "Open orders on old revisions".`
                  : "")
            )
            refreshComponent()
            loadConsumables()
          }}
        />
      )}

      {showLabelsModal && (
        <ConsumableLabelsModal
          items={labelItems}
          initialSelectedIds={dbConsumableId ? [dbConsumableId] : []}
          onClose={() => setShowLabelsModal(false)}
        />
      )}

      {showExposureModal && componentDetail && (
        <ComponentExposureModal
          component={componentDetail}
          onClose={() => setShowExposureModal(false)}
          onApplied={(out) => {
            const n = Object.values(out.updated || {}).reduce((a, b) => a + b, 0)
            setRevisionNotice(out.message || `Moved ${n} reference(s).`)
            refreshComponent()
          }}
        />
      )}
    </div>
  )
}